import { VIEWPORTS, BREAKPOINTS, CHECKS, DEVICE_CLASSES, byClass, type Viewport } from '../lib/responsiveMatrix.ts';
import { derivePreviewUrl } from '../lib/previewUrl.ts';
import type { WizardState } from '../lib/wizardSteps.ts';
import type { Client } from '../lib/types.ts';
import { sectionDesc } from '../lib/ui.ts';

/**
 * Responsive checklist (responsive-checklist.md) — a Design-tab QA section: work the responsive
 * matrix against the client's LIVE preview, ticking each viewport + its 6 sub-CHECKS. Advisory,
 * NEVER gates go-live. Ticks persist in wizard_state.responsive (no migration).
 *
 * ⚠️ Preview = a LAUNCHER, not an iframe (§3): our client sites ship X-Frame-Options: DENY, so an
 * embedded iframe is hard-blocked. window.open(previewUrl, '_blank', 'width=W,height=H') opens a
 * top-level popup sized to the viewport width (what the layout keys off) — respects the header.
 * DPR can't be emulated, so the retina check (CHECKS[5]) is labelled "confirm on a real device".
 */
export function ResponsiveChecklist({ client, ws, onPersist, canMaintainLive = false }: { client: Client; ws: WizardState; onPersist: (next: WizardState) => void; canMaintainLive?: boolean }) {
  const responsive = ws.responsive ?? {};
  // SP-4: wizard-state writes 403 on a live client → read-only. go-live-protection §3a: an admin
  // maintaining a maintainable live client can tick the checklist (wizard-state now admin-writable).
  const liveBlocked = client.is_live && !canMaintainLive;

  // Stable CF branch alias via the canonical resolver (derivePreviewUrl/sanitizeBranchAlias mirror —
  // includes the 28-char alias truncation, so a long slug still opens the REAL preview, not a dead
  // URL). Null before a Pages project exists.
  const previewUrl = client.pages_project ? derivePreviewUrl(client.slug, client.pages_project) : null;

  function setViewport(id: string, checked: boolean) {
    if (liveBlocked) return;
    onPersist({ ...ws, responsive: { ...responsive, [id]: { ...responsive[id], checked } } });
  }
  function setCheck(id: string, idx: number, val: boolean) {
    if (liveBlocked) return;
    const v = responsive[id] ?? {};
    onPersist({ ...ws, responsive: { ...responsive, [id]: { ...v, checks: { ...(v.checks ?? {}), [idx]: val } } } });
  }

  function launch(vp: Viewport) {
    if (!previewUrl) return;
    window.open(previewUrl, '_blank', `width=${vp.width},height=${vp.height}`);
  }

  // Neil (2026-08-11): show ALL viewports — the designer checks every size, so there's no
  // core/additions split in the UI. The `core` flag stays in the data (metadata) but no
  // longer gates display.
  const doneCount = VIEWPORTS.filter((v) => responsive[v.id]?.checked).length;
  const totalVisible = VIEWPORTS.length;

  return (
    <div className="card p-5 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-medium text-ink">Responsive check</p>
          <p className={sectionDesc}>
            Work each screen size against the live preview. Advisory — never blocks go-live.
          </p>
        </div>
        <span className="rounded bg-paper-2 px-2 py-0.5 text-xs text-ink-2">{doneCount}/{totalVisible} viewports</span>
      </div>

      {/* Preview + devtools nudge */}
      <div className="mt-3 flex flex-wrap items-center gap-3 rounded border border-line bg-paper-2 p-2 text-xs">
        {previewUrl ? (
          <a href={previewUrl} target="_blank" rel="noreferrer" className="text-info underline">Open preview ↗</a>
        ) : (
          <span className="text-warning">No preview yet — build &amp; lock a section first.</span>
        )}
        <span className="text-ink-2">
          Each row’s launcher opens a popup sized to that viewport. For pixel-exact / DPR checks, use your browser’s
          <b> devtools device mode</b>.
        </span>
      </div>

      {/* Breakpoints reference */}
      <p className="mt-2 text-[11px] text-ink-2">
        Breakpoints (per-site reference):{' '}
        {BREAKPOINTS.map((b, i) => (
          <span key={b.px}>
            {i > 0 && ' · '}
            <span className="font-mono">{b.px}px</span> {b.name}
          </span>
        ))}
      </p>

      {liveBlocked && (
        <p className="mt-2 rounded border border-warning-bright bg-warning-bright/10 p-2 text-xs text-warning">
          This client is live/protected — the checklist is read-only here (run it pre-launch).
        </p>
      )}

      {/* Viewport groups */}
      <div className="mt-3 space-y-3">
        {DEVICE_CLASSES.map((cls) => {
          const rows = byClass(cls);
          if (rows.length === 0) return null;
          return (
            <div key={cls}>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-2">{cls}</p>
              <ul className="mt-1 space-y-1">
                {rows.map((vp) => {
                  const st = responsive[vp.id] ?? {};
                  return (
                    <li key={vp.id} className="rounded border border-line">
                      <details>
                        <summary className="flex cursor-pointer flex-wrap items-center gap-2 p-2">
                          <input
                            type="checkbox"
                            checked={st.checked === true}
                            disabled={liveBlocked}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setViewport(vp.id, e.target.checked)}
                          />
                          <span className="font-medium text-ink">{vp.label}</span>
                          <span className="font-mono text-[11px] text-ink-2">{vp.width}×{vp.height}</span>
                          <span className="text-[11px] text-ink-2">{vp.aspect}{vp.dpr > 1 ? ` · @${vp.dpr}x` : ''}</span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); launch(vp); }}
                            disabled={!previewUrl}
                            className="ml-auto rounded border border-line px-2 py-0.5 text-[11px] text-ink-2 hover:bg-paper-2 disabled:opacity-40"
                            title={previewUrl ? `Open the preview at ${vp.width}×${vp.height}` : 'No preview yet'}
                          >
                            Open at this size ↗
                          </button>
                        </summary>
                        <div className="border-t border-line p-2">
                          {vp.note && <p className="mb-1 text-[11px] italic text-ink-2">{vp.note}</p>}
                          <ul className="space-y-1">
                            {CHECKS.map((label, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-xs text-ink-2">
                                <input
                                  type="checkbox"
                                  className="mt-0.5"
                                  checked={st.checks?.[idx] === true}
                                  disabled={liveBlocked}
                                  onChange={(e) => setCheck(vp.id, idx, e.target.checked)}
                                />
                                <span>
                                  {label}
                                  {idx === 5 && <span className="text-ink-2"> — confirm on a real device (popup can’t emulate DPR)</span>}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </details>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
